import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { orderBy, limit } from "firebase/firestore";
import { dualReadJobs } from "@/lib/firestore-helpers";
import { useAuth } from "@/contexts/AuthContext";
import { useProjects } from "@/hooks/useProject";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { PermitOpsCenterSkeleton } from "@/components/ui/loading-skeleton";
import { WelcomeEmptyState, FilteredEmptyState } from "@/components/ui/empty-state";
import {
  Plus,
  MapPin,
  ArrowRight,
  Clock,
  AlertCircle,
  CheckCircle2,
  FileSearch,
  Send,
  Award,
  Zap,
  FileText,
  Building2,
  ChevronDown,
  ChevronUp,
  Filter,
  Home,
  Layers,
  FolderKanban,
} from "lucide-react";
import type {
  DevelopmentProject,
  DevelopmentProjectType,
  DevelopmentProjectStatus,
} from "@/types/project";
import { useToast } from "@/hooks/use-toast";
import { sanitizeErrorMessage } from "@/lib/utils";
import { cn } from "@/lib/utils";

import type {
  WorkflowStepId,
  WorkflowStepStatus,
  WorkflowIssue,
  JobWorkflowState,
  WorkflowProgress
} from '@/types/workflow';
import { WORKFLOW_STEPS, getWorkflowStep } from '@/types/workflow';

// Triage status types aligned with the plan
type TriageStatus = 'blocked' | 'review' | 'ready' | 'submitted' | 'issued' | 'all';

// Action priority for sorting (lower = more urgent)
type ActionPriority = 1 | 2 | 3 | 4 | 5;

// Next action derived from workflow state
interface NextAction {
  id: string;
  jobId: string;
  jobTitle: string;
  jobLocation?: string;
  priority: ActionPriority;
  severity: 'blocker' | 'warning' | 'info' | 'ready';
  category: 'missing_data' | 'missing_docs' | 'compliance_issue' | 'review_needed' | 'ready_to_submit';
  message: string;
  details?: string;
  // Deep link to wizard step/substep
  targetStepId: WorkflowStepId;
  targetSubstepId?: string;
  // CTA
  ctaLabel: string;
  ctaVariant: 'default' | 'destructive' | 'outline' | 'secondary';
}

// Job summary for action inbox display
interface JobActionSummary {
  id: string;
  title: string;
  location?: string;
  permitType?: string;
  clientName?: string;
  triageStatus: TriageStatus;
  // Workflow state
  workflow?: {
    currentStepId: WorkflowStepId;
    currentSubstepId?: string;
    stepStatus: Record<WorkflowStepId, WorkflowStepStatus>;
    progress: WorkflowProgress;
    blockerCount: number;
    warningCount: number;
  };
  // Actions derived from workflow
  nextActions: NextAction[];
  // Legacy fields for backwards compat
  complianceStatus?: 'pass' | 'fail' | 'needs_review' | 'not_started' | null;
  complianceScore?: number | null;
  inspectionStatus?: 'pending_verification' | 'verified' | 'scheduled' | 'passed' | 'failed' | null;
  submittedForPermitsAt?: string | null;
  createdAt?: string;
}

// Job type with compliance/inspection fields
type Job = {
  id: string;
  title: string;
  description: string;
  location: string;
  status: "not_started" | "in_progress" | "completed" | "pending_review";
  client_name: string;
  created_at: string;
  permitType?: string;
  complianceStatus?: 'pass' | 'fail' | 'needs_review' | 'not_started' | null;
  complianceScore?: number | null;
  jurisdictionId?: number | null;
  inspectionStatus?: 'pending_verification' | 'verified' | 'scheduled' | 'passed' | 'failed' | null;
  submittedForPermitsAt?: string | null;
  lastComplianceCheck?: string | null;
  // Workflow state from Firestore
  workflow?: JobWorkflowState | null;
};

// Wizard draft from localStorage
type WizardDraft = {
  id: string;
  projectName?: string;
  address?: string;
  currentStep: number;
  totalSteps: number;
  lastUpdated: string;
  trade?: string;
};


// Map job to triage status
function getTriageStatus(job: Job): TriageStatus {
  // ISSUED - inspection passed
  if (job.inspectionStatus === 'passed') return 'issued';
  // SUBMITTED - sent for permit/inspection
  if (job.inspectionStatus === 'verified' || job.inspectionStatus === 'scheduled' || job.submittedForPermitsAt) return 'submitted';
  // BLOCKED - compliance failed or critical issues
  if (job.complianceStatus === 'fail') return 'blocked';
  // REVIEW - needs attention
  if (job.complianceStatus === 'needs_review' || job.inspectionStatus === 'failed') return 'review';
  // READY - compliance passed, ready to submit
  if (job.complianceStatus === 'pass') return 'ready';
  // Default to review if no status
  return 'review';
}

// Status chip config
const statusConfig: Record<TriageStatus, { label: string; icon: React.ElementType; color: string; bgColor: string }> = {
  blocked: { label: 'BLOCKED', icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-950 hover:bg-red-200 dark:hover:bg-red-900' },
  review: { label: 'REVIEW', icon: FileSearch, color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-950 hover:bg-yellow-200 dark:hover:bg-yellow-900' },
  ready: { label: 'READY', icon: CheckCircle2, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-950 hover:bg-green-200 dark:hover:bg-green-900' },
  submitted: { label: 'SUBMITTED', icon: Send, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-950 hover:bg-blue-200 dark:hover:bg-blue-900' },
  issued: { label: 'ISSUED', icon: Award, color: 'text-purple-600', bgColor: 'bg-purple-100 dark:bg-purple-950 hover:bg-purple-200 dark:hover:bg-purple-900' },
  all: { label: 'ALL', icon: Zap, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700' },
};

/**
 * Derive next actions from workflow state
 * This is the core of the "Action Inbox" - it extracts actionable items from workflow state
 */
function deriveNextActions(job: Job): NextAction[] {
  const actions: NextAction[] = [];
  const workflow = job.workflow;

  // If no workflow state, create a "Start Workflow" action
  if (!workflow) {
    // Check if job has any data at all
    const hasBasicData = job.title || job.location;
    if (hasBasicData) {
      actions.push({
        id: `${job.id}-start-workflow`,
        jobId: job.id,
        jobTitle: job.title,
        jobLocation: job.location,
        priority: 2,
        severity: 'warning',
        category: 'review_needed',
        message: 'Start permit preparation',
        details: 'Open the workflow wizard to prepare this job for permit submission.',
        targetStepId: 'basics',
        ctaLabel: 'Start',
        ctaVariant: 'default',
      });
    }
    return actions;
  }

  // Extract blockers as high-priority actions
  workflow.blockers.forEach((issue, idx) => {
    actions.push({
      id: `${job.id}-blocker-${idx}`,
      jobId: job.id,
      jobTitle: job.title,
      jobLocation: job.location,
      priority: 1,
      severity: 'blocker',
      category: issue.category === 'document' ? 'missing_docs' :
               issue.category === 'field' ? 'missing_data' : 'compliance_issue',
      message: issue.message,
      details: issue.details,
      targetStepId: issue.fixStepId,
      targetSubstepId: issue.fixSubstepId,
      ctaLabel: issue.fixAction || 'Fix',
      ctaVariant: 'destructive',
    });
  });

  // Extract warnings as medium-priority actions
  workflow.warnings.forEach((issue, idx) => {
    actions.push({
      id: `${job.id}-warning-${idx}`,
      jobId: job.id,
      jobTitle: job.title,
      jobLocation: job.location,
      priority: 2,
      severity: 'warning',
      category: issue.category === 'document' ? 'missing_docs' :
               issue.category === 'field' ? 'missing_data' : 'review_needed',
      message: issue.message,
      details: issue.details,
      targetStepId: issue.fixStepId,
      targetSubstepId: issue.fixSubstepId,
      ctaLabel: issue.fixAction || 'Review',
      ctaVariant: 'outline',
    });
  });

  // Check workflow progress - if data incomplete, add action
  if (workflow.progress.dataCompletenessPct < 100) {
    // Find first incomplete step
    const incompleteStep = WORKFLOW_STEPS.find(step => {
      const status = workflow.stepStatus[step.id];
      return status === 'pending' || status === 'in_progress';
    });

    if (incompleteStep && !actions.some(a => a.targetStepId === incompleteStep.id && a.severity === 'blocker')) {
      actions.push({
        id: `${job.id}-incomplete-data`,
        jobId: job.id,
        jobTitle: job.title,
        jobLocation: job.location,
        priority: 3,
        severity: 'warning',
        category: 'missing_data',
        message: `Complete ${incompleteStep.shortTitle}`,
        details: `${workflow.progress.dataCompletenessPct}% data complete - continue with ${incompleteStep.title}`,
        targetStepId: incompleteStep.id,
        targetSubstepId: workflow.currentSubstepId,
        ctaLabel: 'Continue',
        ctaVariant: 'default',
      });
    }
  }

  // Check packet completeness
  if (workflow.progress.packetCompletenessPct < 100 && workflow.progress.dataCompletenessPct >= 80) {
    const docsStep = workflow.stepStatus['documents'];
    if (docsStep !== 'ready' && !actions.some(a => a.targetStepId === 'documents' && a.severity === 'blocker')) {
      actions.push({
        id: `${job.id}-incomplete-docs`,
        jobId: job.id,
        jobTitle: job.title,
        jobLocation: job.location,
        priority: 3,
        severity: 'warning',
        category: 'missing_docs',
        message: 'Upload missing documents',
        details: `${workflow.progress.packetCompletenessPct}% documents uploaded`,
        targetStepId: 'documents',
        targetSubstepId: 'requiredDocs',
        ctaLabel: 'Upload',
        ctaVariant: 'outline',
      });
    }
  }

  // If ready for submission (no blockers, high completeness), add "ready" action
  if (
    workflow.blockers.length === 0 &&
    workflow.progress.dataCompletenessPct >= 100 &&
    workflow.progress.packetCompletenessPct >= 100
  ) {
    actions.push({
      id: `${job.id}-ready-submit`,
      jobId: job.id,
      jobTitle: job.title,
      jobLocation: job.location,
      priority: 4,
      severity: 'ready',
      category: 'ready_to_submit',
      message: 'Ready for submission',
      details: 'All requirements met. Review and submit your permit application.',
      targetStepId: 'review',
      targetSubstepId: 'summary',
      ctaLabel: 'Review & Submit',
      ctaVariant: 'default',
    });
  }

  // Sort by priority
  actions.sort((a, b) => a.priority - b.priority);

  return actions;
}

/**
 * Convert job to action summary with derived actions
 */
function toJobActionSummary(job: Job): JobActionSummary {
  const workflow = job.workflow;
  const nextActions = deriveNextActions(job);

  return {
    id: job.id,
    title: job.title,
    location: job.location,
    permitType: job.permitType,
    clientName: job.client_name,
    triageStatus: getTriageStatus(job),
    workflow: workflow ? {
      currentStepId: workflow.currentStepId,
      currentSubstepId: workflow.currentSubstepId,
      stepStatus: workflow.stepStatus,
      progress: workflow.progress,
      blockerCount: workflow.blockers.length,
      warningCount: workflow.warnings.length,
    } : undefined,
    nextActions,
    complianceStatus: job.complianceStatus,
    complianceScore: job.complianceScore,
    inspectionStatus: job.inspectionStatus,
    submittedForPermitsAt: job.submittedForPermitsAt,
    createdAt: job.created_at,
  };
}


// ============================================================================
// Development Projects Section Components (merged from Projects.tsx)
// ============================================================================

/**
 * Get icon for development type
 */
function getProjectTypeIcon(type: DevelopmentProjectType) {
  switch (type) {
    case "townhome":
      return <Home className="h-5 w-5" />;
    case "apartment":
      return <Building2 className="h-5 w-5" />;
    case "subdivision":
      return <Layers className="h-5 w-5" />;
    case "mixed_use":
      return <FolderKanban className="h-5 w-5" />;
    case "commercial_complex":
      return <Building2 className="h-5 w-5" />;
    default:
      return <Home className="h-5 w-5" />;
  }
}

/**
 * Get display name for development type
 */
function getProjectTypeName(type: DevelopmentProjectType): string {
  const names: Record<DevelopmentProjectType, string> = {
    townhome: "Townhome",
    apartment: "Apartment",
    subdivision: "Subdivision",
    mixed_use: "Mixed-Use",
    commercial_complex: "Commercial",
    single_property: "Single Property",
  };
  return names[type] || type;
}

/**
 * Get badge variant for project status
 */
function getProjectStatusBadge(status: DevelopmentProjectStatus) {
  const config: Record<
    DevelopmentProjectStatus,
    { variant: "default" | "secondary" | "outline" | "destructive"; label: string }
  > = {
    planning: { variant: "secondary", label: "Planning" },
    permitting: { variant: "default", label: "Permitting" },
    construction: { variant: "outline", label: "Construction" },
    closeout: { variant: "outline", label: "Closeout" },
    complete: { variant: "secondary", label: "Complete" },
  };
  const { variant, label } = config[status] || { variant: "secondary", label: status };
  return <Badge variant={variant}>{label}</Badge>;
}

const PermitOpsCenter = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<TriageStatus>('all');
  const [wizardDraft, setWizardDraft] = useState<WizardDraft | null>(null);

  const [projectsOpen, setProjectsOpen] = useState(true); // Development projects section

  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, profile: userProfile, loading: authLoading } = useAuth();

  // Development Projects hook
  const { projects, isLoading: projectsLoading } = useProjects();

  // Check for wizard draft in localStorage
  useEffect(() => {
    try {
      const draftStr = localStorage.getItem('wizard_draft');
      if (draftStr) {
        const draft = JSON.parse(draftStr);
        // Only show if draft is less than 7 days old
        const draftDate = new Date(draft.lastUpdated);
        const daysSinceUpdate = (Date.now() - draftDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceUpdate < 7) {
          setWizardDraft(draft);
        } else {
          localStorage.removeItem('wizard_draft');
        }
      }
    } catch (e) {
      console.error('Error reading wizard draft:', e);
    }
  }, []);

  // Fetch jobs
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth");
      return;
    }
    if (!userProfile) return;

    const contractorId = userProfile.contractorId || user.uid;
    fetchJobs(contractorId);
  }, [navigate, user, userProfile, authLoading]);

  const fetchJobs = async (userId: string) => {
    try {
      const jobsSnapshot = await dualReadJobs(userId, [
        orderBy("createdAt", "desc"),
        limit(500)
      ]);

      const jobsData = jobsSnapshot.docs
        .filter((jobDoc) => {
          const jobData = jobDoc.data();
          return !jobData.isDeleted && !jobData.deletedAt && jobData.deleted !== true;
        })
        .map((jobDoc) => {
          const jobData = jobDoc.data();

          // Get compliance data from nested path (how Cloud Function stores it)
          const compliance = jobData.compliance || {};
          const complianceStatus = compliance.status || jobData.complianceStatus;
          const complianceScore = compliance.score || jobData.complianceScore || 0;
          const lastCheckAt = compliance.lastCheckAt || jobData.lastComplianceCheck;

          // Parse check date
          let checkDate: Date | null = null;
          if (lastCheckAt?.toDate) {
            checkDate = lastCheckAt.toDate();
          } else if (lastCheckAt) {
            checkDate = new Date(lastCheckAt);
          }

          return {
            id: jobDoc.id,
            title: jobData.title,
            description: jobData.description || "",
            location: jobData.location || "",
            status: jobData.status,
            client_name: jobData.client_name || "",
            created_at: jobData.createdAt?.toDate().toISOString() || "",
            permitType: jobData.permitType || "",
            complianceStatus: complianceStatus || null,
            complianceScore: complianceScore ?? null,
            jurisdictionId: jobData.jurisdictionId || null,
            inspectionStatus: jobData.inspectionStatus || null,
            submittedForPermitsAt: jobData.submittedForPermitsAt || null,
            lastComplianceCheck: checkDate?.toISOString() || null,
            // Include workflow state for action derivation
            workflow: jobData.workflow || null,
          };
        });

      setJobs(jobsData);

    } catch (error: any) {
      console.error("Error loading jobs:", error);
      toast({
        title: "Error loading permits",
        description: sanitizeErrorMessage(error, "Failed to load permits. Please try again."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Count jobs by triage status
  const statusCounts = useMemo(() => {
    const counts: Record<TriageStatus, number> = {
      blocked: 0,
      review: 0,
      ready: 0,
      submitted: 0,
      issued: 0,
      all: jobs.length,
    };
    jobs.forEach(job => {
      const status = getTriageStatus(job);
      counts[status]++;
    });
    return counts;
  }, [jobs]);

  // Filter jobs by selected status
  const filteredJobs = useMemo(() => {
    if (selectedStatus === 'all') return jobs;
    return jobs.filter(job => getTriageStatus(job) === selectedStatus);
  }, [jobs, selectedStatus]);

  // Convert to action summaries and derive all actions
  const jobActionSummaries = useMemo(() => {
    return jobs.map(toJobActionSummary);
  }, [jobs]);

  // Aggregate all next actions across jobs (for Action Inbox)
  const allNextActions = useMemo(() => {
    const actions = jobActionSummaries.flatMap(job => job.nextActions);
    // Sort by priority (most urgent first)
    return actions.sort((a, b) => a.priority - b.priority);
  }, [jobActionSummaries]);

  // Section: Needs Action (blockers + warnings)
  const needsActionJobs = useMemo(() => {
    return jobActionSummaries.filter(job =>
      job.triageStatus === 'blocked' ||
      job.triageStatus === 'review' ||
      job.nextActions.some(a => a.severity === 'blocker' || a.severity === 'warning')
    );
  }, [jobActionSummaries]);

  // Section: Ready to Submit
  const readyToSubmitJobs = useMemo(() => {
    return jobActionSummaries.filter(job =>
      job.triageStatus === 'ready' ||
      job.nextActions.some(a => a.severity === 'ready')
    );
  }, [jobActionSummaries]);

  // Section: In Progress (submitted but not issued)
  const inProgressJobs = useMemo(() => {
    return jobActionSummaries.filter(job =>
      job.triageStatus === 'submitted'
    );
  }, [jobActionSummaries]);

  // Section: Completed (issued)
  const completedJobs = useMemo(() => {
    return jobActionSummaries.filter(job =>
      job.triageStatus === 'issued'
    );
  }, [jobActionSummaries]);

  // Get filtered action summaries based on selected status
  const filteredActionSummaries = useMemo(() => {
    if (selectedStatus === 'all') return jobActionSummaries;
    return jobActionSummaries.filter(job => job.triageStatus === selectedStatus);
  }, [jobActionSummaries, selectedStatus]);

  // Handle continuing wizard draft
  const handleContinueWizard = () => {
    navigate('/wizard');
  };

  // Handle discarding wizard draft
  const handleDiscardDraft = () => {
    localStorage.removeItem('wizard_draft');
    setWizardDraft(null);
    toast({
      title: "Draft discarded",
      description: "Your in-progress permit intake has been removed.",
    });
  };

  if (loading) {
    return <PermitOpsCenterSkeleton />;
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="My Work" />
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Header - Blockers Remaining as Primary Metric */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          {/* Primary Metric: Blockers Remaining */}
          {statusCounts.blocked > 0 ? (
            <>
              <h1 className="text-3xl font-bold tracking-tight text-red-600">
                {statusCounts.blocked} blocker{statusCounts.blocked !== 1 ? 's' : ''} remaining
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {statusCounts.review > 0 && `${statusCounts.review} items to review • `}
                {statusCounts.ready > 0 && `${statusCounts.ready} ready to submit`}
              </p>
            </>
          ) : statusCounts.review > 0 ? (
            <>
              <h1 className="text-3xl font-bold tracking-tight text-amber-600">
                {statusCounts.review} item{statusCounts.review !== 1 ? 's' : ''} to review
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {statusCounts.ready > 0 && `${statusCounts.ready} ready to submit`}
              </p>
            </>
          ) : statusCounts.ready > 0 ? (
            <>
              <h1 className="text-3xl font-bold tracking-tight text-green-600">
                {statusCounts.ready} ready to submit
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                All blockers cleared
              </p>
            </>
          ) : jobs.length > 0 ? (
            <>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                All caught up
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                No pending actions
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                Action Inbox
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Start your first permit application
              </p>
            </>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => navigate("/jobs/new")}
          className="flex-1 sm:flex-none"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Permit
        </Button>
      </div>

      {/* Resume Where You Left Off */}
      {wizardDraft && (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">Continue Where You Left Off</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <p className="font-medium">
                  {wizardDraft.address || wizardDraft.projectName || 'New Permit Application'}
                  {wizardDraft.trade && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {wizardDraft.trade}
                    </Badge>
                  )}
                </p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>Step {wizardDraft.currentStep} of {wizardDraft.totalSteps}</span>
                  <span>•</span>
                  <span>Last edited: {new Date(wizardDraft.lastUpdated).toLocaleDateString()}</span>
                </div>
                {/* Progress bar */}
                <div className="w-full max-w-xs h-2 bg-muted rounded-full mt-2">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${(wizardDraft.currentStep / wizardDraft.totalSteps) * 100}%` }}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={handleDiscardDraft}>
                  Discard
                </Button>
                <Button size="sm" onClick={handleContinueWizard}>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Development Projects Section */}
      {projects.length > 0 && (
        <Collapsible open={projectsOpen} onOpenChange={setProjectsOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base">Development Projects</CardTitle>
                    <Badge variant="secondary" className="text-xs">{projects.length}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/wizard");
                      }}
                      className="h-7 text-xs"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      New Project
                    </Button>
                    {projectsOpen ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
                <CardDescription>
                  Subdivisions, townhomes, apartments, and multi-unit developments
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {projects.slice(0, 6).map((project) => (
                    <div
                      key={project.id}
                      className="p-4 rounded-lg border bg-card hover:shadow-md transition-all cursor-pointer"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-primary/10 rounded-md">
                            {getProjectTypeIcon(project.developmentType)}
                          </div>
                          <div>
                            <h3 className="font-medium text-sm leading-tight">{project.name}</h3>
                            <p className="text-xs text-muted-foreground">
                              {getProjectTypeName(project.developmentType)}
                            </p>
                          </div>
                        </div>
                        {getProjectStatusBadge(project.status)}
                      </div>

                      {/* Location */}
                      {project.masterAddress && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                          <MapPin className="h-3 w-3" />
                          <span className="truncate">
                            {project.masterAddress.city}, {project.masterAddress.state}
                          </span>
                        </div>
                      )}

                      {/* Stats Row */}
                      <div className="flex items-center justify-between text-xs pt-2 border-t">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{project.totalUnits} units</span>
                          {project.unitStats?.ready > 0 && (
                            <span className="text-green-600">{project.unitStats.ready} ready</span>
                          )}
                          {project.unitStats?.blocked > 0 && (
                            <span className="text-amber-600">{project.unitStats.blocked} blocked</span>
                          )}
                        </div>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
                {projects.length > 6 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-4"
                    onClick={() => navigate("/projects")}
                  >
                    View all {projects.length} projects
                  </Button>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Triage Status Filters */}
      <div className="flex flex-wrap gap-2">
        {(['blocked', 'review', 'ready', 'submitted', 'issued'] as TriageStatus[]).map((status) => {
          const config = statusConfig[status];
          const Icon = config.icon;
          const count = statusCounts[status];
          const isSelected = selectedStatus === status;

          return (
            <button
              key={status}
              onClick={() => setSelectedStatus(isSelected ? 'all' : status)}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all",
                config.bgColor,
                config.color,
                isSelected && "ring-2 ring-offset-2 ring-current"
              )}
            >
              <Icon className="h-4 w-4" />
              <span>{config.label}</span>
              <Badge variant="secondary" className={cn("ml-1", config.color, "bg-white/50 dark:bg-black/20")}>
                {count}
              </Badge>
            </button>
          );
        })}
      </div>

      {/* Action Inbox - Primary View */}
      {selectedStatus === 'all' ? (
        <div className="space-y-4">
          {/* Needs Action Section - Task Card Style */}
          {needsActionJobs.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-sm font-medium text-muted-foreground px-1">
                Needs Action
              </h2>
              {needsActionJobs.slice(0, 8).map((job, index) => {
                const topAction = job.nextActions[0];
                const config = statusConfig[job.triageStatus];
                const Icon = config.icon;
                const isBlocker = topAction?.severity === 'blocker';

                return (
                  <div
                    key={job.id}
                    className={cn(
                      "p-4 rounded-lg border-l-4 transition-all animate-slide-up",
                      isBlocker
                        ? "border-l-red-500 bg-red-50 dark:bg-red-950/20"
                        : "border-l-amber-500 bg-amber-50 dark:bg-amber-950/20"
                    )}
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Status Icon */}
                      <div className={cn("mt-0.5", isBlocker ? "text-red-500" : "text-amber-500")}>
                        <Icon className="h-5 w-5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{job.title}</span>
                          {job.permitType && (
                            <Badge variant="outline" className="text-xs">
                              {job.permitType}
                            </Badge>
                          )}
                        </div>
                        {job.location && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {job.location}
                          </p>
                        )}
                        {/* Action Message */}
                        {topAction && (
                          <p className={cn(
                            "text-sm font-medium mt-2",
                            isBlocker ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400"
                          )}>
                            {topAction.message}
                          </p>
                        )}
                      </div>

                      {/* Single Primary CTA */}
                      {topAction && (
                        <Button
                          size="sm"
                          variant={isBlocker ? "destructive" : "secondary"}
                          onClick={(e) => {
                            e.stopPropagation();
                            const params = new URLSearchParams();
                            params.set('step', topAction.targetStepId);
                            if (topAction.targetSubstepId) {
                              params.set('substep', topAction.targetSubstepId);
                            }
                            navigate(`/jobs/${job.id}?${params.toString()}`);
                          }}
                        >
                          {topAction.ctaLabel}
                          <ArrowRight className="h-3 w-3 ml-1" />
                        </Button>
                      )}
                    </div>
                    {/* Additional actions indicator */}
                    {job.nextActions.length > 1 && (
                      <p className="text-xs text-muted-foreground mt-2 pl-8">
                        +{job.nextActions.length - 1} more {job.nextActions.length - 1 === 1 ? 'action' : 'actions'}
                      </p>
                    )}
                  </div>
                );
              })}
              {needsActionJobs.length > 8 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => setSelectedStatus('blocked')}
                >
                  View all {needsActionJobs.length} needing action
                </Button>
              )}
            </div>
          )}

          {/* Ready to Submit Section - Task Card Style */}
          {readyToSubmitJobs.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-sm font-medium text-muted-foreground px-1">
                Ready to Submit
              </h2>
              {readyToSubmitJobs.slice(0, 5).map((job, index) => (
                <div
                  key={job.id}
                  className="p-4 rounded-lg border-l-4 border-l-green-500 bg-green-50 dark:bg-green-950/20 transition-all animate-slide-up"
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  <div className="flex items-start gap-3">
                    {/* Status Icon */}
                    <div className="mt-0.5 text-green-500">
                      <CheckCircle2 className="h-5 w-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{job.title}</span>
                        {job.permitType && (
                          <Badge variant="outline" className="text-xs">
                            {job.permitType}
                          </Badge>
                        )}
                      </div>
                      {job.location && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3 w-3" />
                          {job.location}
                        </p>
                      )}
                      <p className="text-sm font-medium text-green-700 dark:text-green-400 mt-2">
                        All requirements complete
                      </p>
                    </div>

                    {/* Single Primary CTA */}
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/jobs/${job.id}?step=review&substep=summary`);
                      }}
                    >
                      Submit
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
              {readyToSubmitJobs.length > 5 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => setSelectedStatus('ready')}
                >
                  View all {readyToSubmitJobs.length} ready
                </Button>
              )}
            </div>
          )}

          {/* In Progress Section (Submitted) */}
          {inProgressJobs.length > 0 && (
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Send className="h-5 w-5 text-blue-500" />
                    <CardTitle className="text-base">In Progress</CardTitle>
                    <Badge className="text-xs bg-blue-600">
                      {inProgressJobs.length}
                    </Badge>
                  </div>
                </div>
                <CardDescription>
                  Jobs submitted and awaiting permit issuance
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {inProgressJobs.slice(0, 3).map((job, index) => (
                    <div
                      key={job.id}
                      className="p-4 hover:bg-muted/30 cursor-pointer transition-all animate-slide-up"
                      style={{ animationDelay: `${index * 30}ms` }}
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Send className="h-4 w-4 text-blue-500 shrink-0" />
                            <span className="font-medium truncate">{job.title}</span>
                            {job.permitType && (
                              <Badge variant="outline" className="shrink-0 text-xs">
                                {job.permitType}
                              </Badge>
                            )}
                          </div>
                          {job.location && (
                            <div className="flex items-center text-sm text-muted-foreground">
                              <MapPin className="h-3 w-3 mr-1 shrink-0" />
                              <span className="truncate">{job.location}</span>
                            </div>
                          )}
                        </div>
                        <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                          Submitted
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
                {inProgressJobs.length > 3 && (
                  <div className="p-3 border-t bg-muted/20">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => setSelectedStatus('submitted')}
                    >
                      View all {inProgressJobs.length} in-progress jobs
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Completed Section */}
          {completedJobs.length > 0 && (
            <Collapsible>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="pb-2 cursor-pointer hover:bg-muted/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Award className="h-5 w-5 text-purple-500" />
                        <CardTitle className="text-base">Completed</CardTitle>
                        <Badge variant="secondary" className="text-xs">
                          {completedJobs.length}
                        </Badge>
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <CardDescription>
                      Jobs with issued permits
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {completedJobs.slice(0, 5).map((job, index) => (
                        <div
                          key={job.id}
                          className="p-4 hover:bg-muted/30 cursor-pointer transition-all"
                          onClick={() => navigate(`/jobs/${job.id}`)}
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Award className="h-4 w-4 text-purple-500 shrink-0" />
                                <span className="font-medium truncate">{job.title}</span>
                              </div>
                              {job.location && (
                                <div className="flex items-center text-sm text-muted-foreground">
                                  <MapPin className="h-3 w-3 mr-1 shrink-0" />
                                  <span className="truncate">{job.location}</span>
                                </div>
                              )}
                            </div>
                            <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300">
                              Issued
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}

          {/* Empty State */}
          {jobs.length === 0 && (
            <WelcomeEmptyState
              userName={userProfile?.displayName?.split(' ')[0]}
              onStartWizard={() => navigate("/wizard")}
              onQuickCreate={() => navigate("/quick-create")}
            />
          )}
        </div>
      ) : (
        /* Filtered View - Shows all jobs matching selected status */
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  {statusConfig[selectedStatus].label} Permits
                </CardTitle>
                <CardDescription>
                  {filteredActionSummaries.length} {filteredActionSummaries.length === 1 ? 'permit' : 'permits'}
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedStatus('all')}>
                <Filter className="h-4 w-4 mr-1" />
                Clear filter
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {filteredActionSummaries.length === 0 ? (
              <FilteredEmptyState
                filterName="Status"
                filterValue={statusConfig[selectedStatus].label}
                onClearFilter={() => setSelectedStatus('all')}
              />
            ) : (
              <div className="divide-y">
                {filteredActionSummaries.map((job, index) => {
                  const topAction = job.nextActions[0];
                  const config = statusConfig[job.triageStatus];
                  const Icon = config.icon;

                  return (
                    <div
                      key={job.id}
                      className="p-4 hover:bg-muted/50 transition-all animate-slide-up"
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className={cn("h-4 w-4 shrink-0", config.color)} />
                            <span className="font-medium truncate">{job.title}</span>
                            {job.permitType && (
                              <Badge variant="outline" className="shrink-0 text-xs">
                                {job.permitType}
                              </Badge>
                            )}
                          </div>
                          {job.location && (
                            <div className="flex items-center text-sm text-muted-foreground mb-2">
                              <MapPin className="h-3 w-3 mr-1 shrink-0" />
                              <span className="truncate">{job.location}</span>
                            </div>
                          )}
                          {job.clientName && (
                            <p className="text-sm text-muted-foreground">
                              Client: {job.clientName}
                            </p>
                          )}
                          {/* Action items */}
                          {topAction && (
                            <div className={cn(
                              "mt-2 p-2 rounded-md text-sm",
                              topAction.severity === 'blocker' ? 'bg-red-50 dark:bg-red-950/30' :
                              topAction.severity === 'warning' ? 'bg-amber-50 dark:bg-amber-950/30' :
                              topAction.severity === 'ready' ? 'bg-green-50 dark:bg-green-950/30' :
                              'bg-muted/50'
                            )}>
                              <span className="font-medium">{topAction.message}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <Badge
                            variant="secondary"
                            className={cn("text-xs", config.bgColor, config.color)}
                          >
                            {config.label}
                          </Badge>
                          {topAction && (
                            <Button
                              size="sm"
                              variant={topAction.ctaVariant}
                              onClick={(e) => {
                                e.stopPropagation();
                                const params = new URLSearchParams();
                                params.set('step', topAction.targetStepId);
                                if (topAction.targetSubstepId) {
                                  params.set('substep', topAction.targetSubstepId);
                                }
                                navigate(`/jobs/${job.id}?${params.toString()}`);
                              }}
                            >
                              {topAction.ctaLabel}
                              <ArrowRight className="h-3 w-3 ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
    </div>
  );
};

export default PermitOpsCenter;
